import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { todayIso, t } from '@procumi/shared';

/**
 * Câmpul de dată al aplicației: arată mereu zz.ll.aaaa și un calendar în română.
 * `<input type="date">` urmează limba browserului (pe un Chrome în engleză:
 * 10/18/2026 și calendar în engleză), deci nu-l mai folosim.
 *
 * Valoarea rămâne 'AAAA-LL-ZZ' (sau ''), ca la câmpul nativ: locurile care îl
 * foloseau nu se schimbă decât la `onChange`, care primește direct data.
 */
const MONTHS = [
  'ianuarie',
  'februarie',
  'martie',
  'aprilie',
  'mai',
  'iunie',
  'iulie',
  'august',
  'septembrie',
  'octombrie',
  'noiembrie',
  'decembrie',
];
const WEEKDAYS = ['L', 'Ma', 'Mi', 'J', 'V', 'S', 'D'];

const pad = (n: number) => String(n).padStart(2, '0');
const isoOf = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;

/** 'AAAA-LL-ZZ' → '18.10.2026' */
export const dotDate = (iso: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '';

/** „18.10.2026”, „1.5.2026”, „1.5.26”, „18102026” → 'AAAA-LL-ZZ', sau null dacă nu e o zi reală */
function parseDot(s: string): string | null {
  const t = s.trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t); // lipită dintr-un export sau dintr-un script
  if (iso) return parseDot(`${iso[3]}.${iso[2]}.${iso[1]}`);
  const m = /^(\d{1,2})[./-](\d{1,2})[./-](\d{2}|\d{4})$/.exec(t) ?? /^(\d{2})(\d{2})(\d{4})$/.exec(t);
  if (!m) return null;
  const d = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const y = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
  const dt = new Date(y, mo, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) return null;
  return isoOf(y, mo, d);
}

/** cifrele scrise una după alta primesc punctele singure: 1810 → 18.10. */
function mask(raw: string): string {
  const clean = raw.replace(/[^\d./-]/g, '');
  // separatorii puși de om în altă parte („1.5.2026”, „2026-10-18”) rămân cum i-a scris
  if ([...clean].some((c, i) => /\D/.test(c) && i !== 2 && i !== 5)) return clean.slice(0, 10);
  const s = clean.replace(/\D/g, '').slice(0, 8);
  if (s.length <= 2) return s;
  if (s.length <= 4) return `${s.slice(0, 2)}.${s.slice(2)}`;
  return `${s.slice(0, 2)}.${s.slice(2, 4)}.${s.slice(4)}`;
}

export function DateInput({
  value,
  onChange,
  min,
  max,
  id,
  disabled,
  title,
}: {
  value: string;
  onChange: (iso: string) => void;
  min?: string;
  max?: string;
  id?: string;
  disabled?: boolean;
  title?: string;
}) {
  const [text, setText] = useState(dotDate(value));
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLInputElement>(null);

  // valoarea schimbată din afară (ciornă încărcată, „Azi”) ajunge în câmp
  useEffect(() => {
    if (document.activeElement !== field.current) setText(dotDate(value));
  }, [value]);

  const outside = (iso: string) => (min && iso < min) || (max && iso > max);

  const type = (raw: string) => {
    const t = mask(raw);
    setText(t);
    if (t === '') return onChange('');
    const iso = parseDot(t);
    // anul scurt („1.5.26”) se primește doar la ieșire, ca „18.10.20” să nu devină 2020 din mers
    if (iso && /[./-]\d{4}$|^\d{8}$|^\d{4}-\d{2}-\d{2}$/.test(t)) onChange(iso);
  };

  // la ieșire: „1.5.26” devine 01.05.2026; ce nu e o dată se întoarce la valoarea bună
  const settle = () => {
    const iso = parseDot(text);
    if (iso) {
      setText(dotDate(iso));
      if (iso !== value) onChange(iso);
    } else if (text.trim() === '') {
      if (value) onChange('');
    } else setText(dotDate(value));
  };

  const pick = (iso: string) => {
    setText(dotDate(iso));
    onChange(iso);
    setOpen(false);
    field.current?.focus();
  };

  const bad = text.length >= 8 && (!parseDot(text) || Boolean(outside(parseDot(text) ?? '')));

  return (
    <div className="date-inp" ref={box}>
      <input
        ref={field}
        id={id}
        className="inp"
        inputMode="numeric"
        autoComplete="off"
        placeholder={t('zz.ll.aaaa')}
        value={text}
        disabled={disabled}
        title={title}
        aria-invalid={bad || undefined}
        onChange={(e) => type(e.target.value)}
        onBlur={settle}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && e.altKey) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      />
      <button
        type="button"
        className="date-btn"
        aria-label={t('Alege data din calendar')}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen(!open)}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <rect x="2.5" y="3.5" width="11" height="10" rx="1.5" />
          <path d="M2.5 6.5h11M5.5 2v3M10.5 2v3" />
        </svg>
      </button>
      {open ? (
        <Calendar
          anchor={box}
          value={value}
          min={min}
          max={max}
          onPick={pick}
          onClose={() => {
            setOpen(false);
            field.current?.focus();
          }}
        />
      ) : null}
    </div>
  );
}

function Calendar({
  anchor,
  value,
  min,
  max,
  onPick,
  onClose,
}: {
  anchor: React.RefObject<HTMLDivElement | null>;
  value: string;
  min?: string;
  max?: string;
  onPick: (iso: string) => void;
  onClose: () => void;
}) {
  const today = todayIso();
  const start = value || (min && today < min ? min : max && today > max ? max : today);
  const [focus, setFocus] = useState(start);
  const y = Number(focus.slice(0, 4));
  const m = Number(focus.slice(5, 7)) - 1;
  const pop = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // fix față de ecran, ca dialogurile cu derulare să nu-l taie; dacă un părinte
  // cu `transform` mută originea, corectăm cu cât s-a deplasat de fapt
  useLayoutEffect(() => {
    const a = anchor.current?.getBoundingClientRect();
    const p = pop.current;
    if (!a || !p) return;
    const w = p.offsetWidth;
    const h = p.offsetHeight;
    const top = a.bottom + 6 + h > window.innerHeight - 8 && a.top - 6 - h > 8 ? a.top - 6 - h : a.bottom + 6;
    const left = Math.max(8, Math.min(a.left, window.innerWidth - w - 8));
    p.style.top = `${top}px`;
    p.style.left = `${left}px`;
    const r = p.getBoundingClientRect();
    setPos({ top: top + (top - r.top), left: left + (left - r.left) });
  }, [anchor]);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (!anchor.current?.contains(e.target as Node)) onClose();
    };
    const away = (e: Event) => {
      if (!pop.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', away, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', away, true);
      window.removeEventListener('resize', onClose);
    };
  }, [anchor, onClose]);

  // ziua aleasă cu tastele primește focusul
  useEffect(() => {
    pop.current?.querySelector<HTMLButtonElement>(`[data-iso="${focus}"]`)?.focus();
  }, [focus, pos]);

  const off = (iso: string) => Boolean((min && iso < min) || (max && iso > max));
  const move = (days: number) => {
    const d = new Date(y, m, Number(focus.slice(8, 10)) + days);
    setFocus(isoOf(d.getFullYear(), d.getMonth(), d.getDate()));
  };
  const month = (delta: number) => {
    const last = new Date(y, m + delta + 1, 0).getDate();
    setFocus(
      isoOf(
        new Date(y, m + delta, 1).getFullYear(),
        new Date(y, m + delta, 1).getMonth(),
        Math.min(Number(focus.slice(8, 10)), last),
      ),
    );
  };

  const lead = (new Date(y, m, 1).getDay() + 6) % 7; // luni = 0
  const days = new Date(y, m + 1, 0).getDate();
  const cells: (string | null)[] = [
    ...Array<null>(lead).fill(null),
    ...Array.from({ length: days }, (_, i) => isoOf(y, m, i + 1)),
  ];

  return (
    <div
      ref={pop}
      className="date-pop"
      role="dialog"
      aria-label={t('Calendar')}
      style={pos ?? { visibility: 'hidden' }}
      onKeyDown={(e) => {
        const step: Record<string, number> = {
          ArrowLeft: -1,
          ArrowRight: 1,
          ArrowUp: -7,
          ArrowDown: 7,
        };
        if (e.key === 'Escape') {
          e.preventDefault(); // altfel <dialog> se închide și el
          e.stopPropagation();
          onClose();
        } else if (step[e.key]) {
          e.preventDefault();
          move(step[e.key]);
        } else if (e.key === 'PageUp' || e.key === 'PageDown') {
          e.preventDefault();
          month(e.key === 'PageUp' ? -1 : 1);
        }
      }}
    >
      <div className="date-head">
        <button type="button" className="date-nav" aria-label={t('Luna trecută')} onClick={() => month(-1)}>
          ‹
        </button>
        <span aria-live="polite">
          {MONTHS[m]} {y}
        </span>
        <button type="button" className="date-nav" aria-label={t('Luna viitoare')} onClick={() => month(1)}>
          ›
        </button>
      </div>
      <div className="date-grid" role="grid">
        {WEEKDAYS.map((w) => (
          <span key={w} className="date-wd" aria-hidden="true">
            {w}
          </span>
        ))}
        {cells.map((iso, i) =>
          iso ? (
            <button
              key={iso}
              type="button"
              data-iso={iso}
              tabIndex={iso === focus ? 0 : -1}
              disabled={off(iso)}
              aria-pressed={iso === value}
              aria-label={`${Number(iso.slice(8, 10))} ${MONTHS[m]} ${y}`}
              className={iso === today ? 'today' : undefined}
              onClick={() => onPick(iso)}
            >
              {Number(iso.slice(8, 10))}
            </button>
          ) : (
            <span key={`e${i}`} />
          ),
        )}
      </div>
      <div className="date-foot">
        <button type="button" disabled={off(today)} onClick={() => onPick(today)}>
          {t('Azi')}
        </button>
        {value ? (
          <button type="button" onClick={() => onPick('')}>
            {t('Șterge')}
          </button>
        ) : null}
      </div>
    </div>
  );
}
